import type { QueryClient } from '@tanstack/react-query';
import { mailServerKeys, type MailServer } from './entity';

/**
 * Mail-server cache helpers.
 *
 * The list has no filter dimension, so there is a single list slot
 * (`keys.list(wsId, undefined)`) holding the ordered array. Promoting one
 * server to a default demotes its siblings server-side, so that response
 * carries every server and replaces the list wholesale via
 * `setMailServersCache` rather than patching one row.
 */

/** Write one server into its detail slot. */
export function setMailServerCache(qc: QueryClient, workspaceId: number, server: MailServer): void {
  qc.setQueryData(mailServerKeys.detail(workspaceId, server.id), server);
}

/**
 * Replace the list slot and every detail slot from an authoritative
 * whole-collection response.
 */
export function setMailServersCache(qc: QueryClient, workspaceId: number, servers: readonly MailServer[]): void {
  qc.setQueryData(mailServerKeys.list(workspaceId, undefined), servers);
  for (const server of servers) {
    setMailServerCache(qc, workspaceId, server);
  }
}

/** Drop a deleted server's detail slot. */
export function removeMailServerCache(qc: QueryClient, workspaceId: number, id: string): void {
  qc.removeQueries({ queryKey: mailServerKeys.detail(workspaceId, id) });
}

/** Force the cached list to refetch after a membership change. */
export function invalidateMailServerLists(qc: QueryClient, workspaceId: number): void {
  void qc.invalidateQueries({ queryKey: mailServerKeys.lists(workspaceId) });
}
